/** @file Root-level exports for `whiteboard-tiptap` */
import type { Content } from '@tiptap/core'
import { AnyExtension, Editor } from '@tiptap/core'
import { Color } from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import { tipTapDefaultExtensions } from 'tldraw'
import { DisabledLink } from './extensions/disabled_link'
import { whiteboardTiptapTextDirectionExtensions } from './extensions/text_direction'
import './tiptap_types'

/**
 * Default extensions used by the tldraw tiptap editor
 *
 * - We disable the Link extension as per https://github.com/padlet/mozart/pull/37793 for safety reasons
 * - We disable the TextDirection extension as we handle it ourselves in whiteboardTiptapTextDirectionExtensions
 */
const defaultExtensions = [
  ...tipTapDefaultExtensions.filter((ext) => !['link', 'textDirection'].includes(ext.name)),
  DisabledLink,
  TextStyle,
  Color,
] as AnyExtension[]

/**
 * Create a new Tiptap editor for whiteboard text shapes
 * @param content The content to initialize the editor with
 * @param context Required parameter to initialize the editor with the correct text direction
 * @returns A new Tiptap editor instance
 */
function createTiptapEditor({ content, context }: { content: Content; context: { direction: 'ltr' | 'rtl' } }): Editor {
  return new Editor({
    extensions: [...defaultExtensions, ...whiteboardTiptapTextDirectionExtensions(context.direction)],
    content,
  })
}

export * from '@tiptap/core'
export { Plugin, PluginKey, type EditorState, type PluginView } from '@tiptap/pm/state'
export { EditorView } from '@tiptap/pm/view'
export { createTiptapEditor, defaultExtensions, whiteboardTiptapTextDirectionExtensions }
